import React from 'react';
import { X } from 'lucide-react';
import { AppModal } from '../../../../components/AppModal';
import { AppSelect } from '../../../../components/AppSelect';
import { AutoResizeTextarea } from '../../../../components/AutoResizeTextarea';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import {
  VAT_OPTIONS,
  MATERIAL_UNIT_OPTIONS,
  formatMaterialPrice,
} from '../../utils/materialForm';

const FIELD_CLASS =
  'w-full h-9 px-3 py-0 border border-app-border rounded-md text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none';

export type MaterialCreateModalProps = {
  open: boolean;
  onClose: () => void;
  saving: boolean;
  previewVk: number;
  error?: string | null;
  name: string;
  brand: string;
  supplier: string;
  gtin: string;
  unit: string;
  ekPrice: string;
  surchargePercent: string;
  vatPercent: string;
  description: string;
  minutes: string;
  onNameChange: (v: string) => void;
  onBrandChange: (v: string) => void;
  onSupplierChange: (v: string) => void;
  onGtinChange: (v: string) => void;
  onUnitChange: (v: string) => void;
  onEkPriceChange: (v: string) => void;
  onSurchargePercentChange: (v: string) => void;
  onVatPercentChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  onMinutesChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
};

export function MaterialCreateModal({
  open,
  onClose,
  saving,
  previewVk,
  error,
  name,
  brand,
  supplier,
  gtin,
  unit,
  ekPrice,
  surchargePercent,
  vatPercent,
  description,
  minutes,
  onNameChange,
  onBrandChange,
  onSupplierChange,
  onGtinChange,
  onUnitChange,
  onEkPriceChange,
  onSurchargePercentChange,
  onVatPercentChange,
  onDescriptionChange,
  onMinutesChange,
  onSubmit,
}: MaterialCreateModalProps) {
  const canSubmit =
    !saving && Boolean(name.trim() && unit.trim() && ekPrice.trim() && surchargePercent.trim());

  return (
    <AppModal
      open={open}
      onClose={onClose}
      disableClose={saving}
      overlayClassName="z-50"
      panelClassName="md:max-w-3xl"
      ariaLabelledBy="material-create-title"
    >
      <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-app-border px-5 py-3">
          <h2 id="material-create-title" className="text-sm font-semibold text-app-text">
            Neues Material
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-app-border text-app-text-subtle hover:bg-app-hover hover:text-app-text disabled:opacity-40"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {error ? (
          <div className="mx-5 mt-3 shrink-0 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="space-y-4 overflow-y-auto p-5">
          <div className="grid grid-cols-1 items-end gap-3 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs font-medium text-app-text-muted">
                Name<span className="text-red-500">*</span>
              </label>
              <input
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                className={FIELD_CLASS}
                placeholder="z.B. Wärmepumpe 9 kW"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-app-text-muted">Marke</label>
              <input
                value={brand}
                onChange={(e) => onBrandChange(e.target.value)}
                className={FIELD_CLASS}
                placeholder="z.B. Vaillant"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-app-text-muted">Lieferant</label>
              <input
                value={supplier}
                onChange={(e) => onSupplierChange(e.target.value)}
                className={FIELD_CLASS}
                placeholder="Großhändler"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-app-text-muted">G-Handels Nr.</label>
              <input
                value={gtin}
                onChange={(e) => onGtinChange(e.target.value)}
                className={FIELD_CLASS}
                placeholder="G-Handels Nr. / EAN"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-app-text-muted">
                Einheit<span className="text-red-500">*</span>
              </label>
              <AppSelect
                value={unit}
                onChange={(v) => onUnitChange(v || '')}
                options={MATERIAL_UNIT_OPTIONS}
                size="sm"
                triggerClassName="!min-h-0 h-9 !py-0"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-app-text-muted">
                Einkaufspreis (EK)<span className="text-red-500">*</span>
              </label>
              <input
                value={ekPrice}
                onChange={(e) => onEkPriceChange(e.target.value)}
                className={FIELD_CLASS}
                placeholder="z.B. 1999,00"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-app-text-muted">
                Zuschlag (%)<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={surchargePercent}
                onChange={(e) => onSurchargePercentChange(e.target.value)}
                className={FIELD_CLASS}
                placeholder="z. B. 25 oder 0"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-app-text-muted">
                MwSt.<span className="text-red-500">*</span>
              </label>
              <AppSelect
                value={vatPercent}
                onChange={(v) => onVatPercentChange(v || '19')}
                options={VAT_OPTIONS}
                size="sm"
                triggerClassName="!min-h-0 h-9 !py-0"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-app-text-muted">Minuten (Einbau)</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={minutes}
                onChange={(e) => onMinutesChange(e.target.value.replace(/\D+/g, ''))}
                className={FIELD_CLASS}
                placeholder="z. B. 45"
              />
            </div>
            <div className="rounded-md border border-app-border bg-app-surface-muted px-3 py-2 text-sm text-app-text-muted md:col-span-2">
              <span className="font-medium text-app-text-muted">VK (berechnet):</span>{' '}
              <span className="tabular-nums">{formatMaterialPrice(previewVk)}</span>
              <span className="ml-2 text-xs text-app-text-subtle">EK × (1 + Zuschlag % / 100)</span>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-app-text-muted">Beschreibung</label>
            <AutoResizeTextarea
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              rows={3}
              maxHeight={240}
              className="min-h-[72px] w-full resize-none rounded-md border border-app-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-pipedrive-blue/20"
              placeholder="Optionale Beschreibung des Materials…"
            />
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-app-border px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-9 items-center justify-center rounded-md border border-app-border bg-app-surface px-3.5 text-sm font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-40"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-pipedrive-blue px-3.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40"
          >
            {saving ? <LoadingSpinner size="sm" /> : null}
            Material anlegen
          </button>
        </div>
      </form>
    </AppModal>
  );
}
